import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { JOB_ORDER_STATUSES } from '../../../constants/jobOrderConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import {
  redesignCorpSx,
  redesignEmployeesStyle,
  redesignTitleSx,
  redesignTotalLabelSx,
  redesignTotalValueSx,
} from '../../JobOrderCard/redesignRosterType';
import { getTotalJobHours } from '../helperFunctions';

import EmployeeRosterCard from './SubComponents/EmployeeRosterCard';

const useStyles = makeStyles({
  content: {
    padding: '12px',
    fontFamily: PRIMARY_FONT[300],
    overflow: 'auto',
  },
  titleText: {
    width: '100%',
    display: 'block',
    textAlign: 'left',
    fontSize: '20px',
    fontFamily: PRIMARY_FONT[700],
    lineHeight: '16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  corpNameText: {
    maxWidth: '280px',
    display: 'block',
    textAlign: 'left',
    fontSize: '14px',
    fontFamily: PRIMARY_FONT[300],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: '3px',
  },

  employees: {
    display: 'flex',
    flexDirection: 'column',
    '& .title': {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '10px',
      lineHeight: '12px',
      fontFamily: PRIMARY_FONT[300],
      paddingTop: '12px',
      textTransform: 'uppercase',
      '& .listHeader': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
    },
    '& .reserveListTitle': {
      display: 'flex',
      justifyContent: 'flex-end',
      fontSize: '10px',
      lineHeight: '12px',
      fontWeight: 300,
      textTransform: 'uppercase',
      '& .listHeader': {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      },
    },
    '& .list': {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
  },
  totalJobHours: {
    textAlign: 'right',
    fontSize: '16px',
    paddingTop: '8px',
    fontFamily: PRIMARY_FONT[500],
  },
  infoIcon: { fontSize: '14px' },

  reserveListText: {
    fontFamily: PRIMARY_FONT[700],
    textAlign: 'left',
    fontSize: '12px',
    lineHeight: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textTransform: 'uppercase',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
});

const CompletedRosterView = ({
  jobOrder,
  isExpanded,
  clearTimeCardError,
  handleTimecardEditSubmit,
  onStatusUpdate,
  timecardUpdateError,
  timecardIsUpdating,
  redesign = false,
}) => {
  const classes = useStyles();
  const numOpenings = jobOrder?.numOpenings || 0;
  const placements =
    jobOrder?.placements?.filter((p) => p?.state?.status === JOB_ORDER_STATUSES.ACTIVE) || [];

  const sortedPlacements = placements?.sort(
    (a, b) => new Date(a?.metadata?.created) - new Date(b?.metadata?.created)
  );
  const activePlacements = sortedPlacements.slice(0, numOpenings);
  const reserveList = sortedPlacements.slice(numOpenings);

  return (
    <Box>
      <Box
        className={classes.content}
        sx={{
          height: isExpanded ? '740px' : '324px',
        }}
      >
        <div className={classes.employees}>
          <Typography className={classes.titleText} sx={redesign ? redesignTitleSx : undefined}>
            {jobOrder?.name}
          </Typography>
          {jobOrder?.corporation?.name && (
            <Box sx={{ display: 'flex' }}>
              <Typography
                className={classes.corpNameText}
                sx={redesign ? redesignCorpSx : undefined}
              >
                {jobOrder?.corporation?.name}
              </Typography>
            </Box>
          )}
          <div className="title">
            <div style={redesign ? redesignEmployeesStyle : undefined}>
              {`${LanguageConverter('jobOrder.employees')}${redesign ? ' ' : ''}(${
                placements?.length
              })`}
            </div>
          </div>
          <div className="list">
            {activePlacements.map((placement) => (
              <EmployeeRosterCard
                key={placement._id}
                placement={placement}
                corporationName={jobOrder?.corporation?.name}
                clearTimeCardError={clearTimeCardError}
                shiftName={jobOrder?.name}
                handleTimecardEditSubmit={handleTimecardEditSubmit}
                onStatusUpdate={onStatusUpdate}
                timecardUpdateError={timecardUpdateError}
                timecardIsUpdating={timecardIsUpdating}
                redesign={redesign}
              />
            ))}
            {reserveList.length > 0 && (
              <>
                <Divider className={classes.reserveListText}>
                  {LanguageConverter('roster.reserveList')}
                </Divider>

                <div className="list">
                  {reserveList.map((placement) => (
                    <EmployeeRosterCard
                      key={placement._id}
                      placement={placement}
                      corporationName={jobOrder?.corporation?.name}
                      clearTimeCardError={clearTimeCardError}
                      shiftName={jobOrder?.name}
                      handleTimecardEditSubmit={handleTimecardEditSubmit}
                      onStatusUpdate={onStatusUpdate}
                      timecardUpdateError={timecardUpdateError}
                      timecardIsUpdating={timecardIsUpdating}
                      redesign={redesign}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          {redesign ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                gap: '16px',
                pt: '12px',
              }}
            >
              <Typography sx={redesignTotalLabelSx}>
                {`${LanguageConverter('jobOrder.totalHours')}:`}
              </Typography>
              <Typography sx={redesignTotalValueSx}>
                {getTotalJobHours(placements).toFixed(2)}
              </Typography>
            </Box>
          ) : (
            <Typography className={classes.totalJobHours}>
              {`${LanguageConverter('jobOrder.totalHours')} ${getTotalJobHours(placements).toFixed(
                2
              )}`}
            </Typography>
          )}
        </div>
      </Box>
    </Box>
  );
};

CompletedRosterView.propTypes = {
  isExpanded: PropTypes.bool,
  jobOrder: PropTypes.shape({
    _id: PropTypes.string,
    corporation: PropTypes.shape({
      name: PropTypes.string,
    }),
    name: PropTypes.string,
    numOpenings: PropTypes.number,
    placements: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      })
    ),
    start: PropTypes.number,
    end: PropTypes.number,
    metadata: PropTypes.shape({
      bullhorn: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
  }),
  clearTimeCardError: PropTypes.func,
  handleTimecardEditSubmit: PropTypes.func,
  onStatusUpdate: PropTypes.func,
  timecardUpdateError: PropTypes.string,
  timecardIsUpdating: PropTypes.bool,
  redesign: PropTypes.bool,
};

export default CompletedRosterView;
